import { useState } from 'react';
import { useAnalytics } from '@/services/analytics';
import type { ObjectType } from '@/services/db/object-type/entity';
import { ImportType } from '@/services/db/sql-import/entity';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import {
  buildPreviewBody,
  defaultSqlImportQuery,
  previewFingerprint,
  sqlImportSyncTargetKind,
  usePreviewRun,
  type SqlImportRelationship,
} from '@/features/sql-sync';
import { isEmptyString } from '@/utils/string';
import type { SqlImportDraft } from './draft';
import { relationshipForStep, resyncedRelationship } from './relationships';
import type { WizardStep } from './steps';

type UseQueryStepsArgs = {
  workspaceId: number;
  type: SqlDatabaseType;
  databaseTypeName: string;
  syncTarget: string;
  draft: SqlImportDraft;
  /** The saved import the draft belongs to, once it has one. */
  savedId: string | null;
  objectTypes: readonly Pick<ObjectType, 'id' | 'name' | 'enabled'>[];
  setDraftFields: (changes: Partial<SqlImportDraft>) => void;
  /** Move on from the current step, as "Next" would. */
  advance: () => void;
};

/**
 * The Query and Relationships steps' state: whether each query previewed successfully (a run is
 * required before moving on, and any edit asks for another), the preview runs themselves, the
 * relationship being edited, and the timeout override and skip actions. Components are layout.
 */
export function useQuerySteps({
  workspaceId,
  type,
  databaseTypeName,
  syncTarget,
  draft,
  savedId,
  objectTypes,
  setDraftFields,
  advance,
}: UseQueryStepsArgs) {
  const { track } = useAnalytics();
  // What each step last previewed successfully; the step is unlocked while its body still matches.
  const [queryVouched, setQueryVouched] = useState<string | null>(null);
  const [relationshipsVouched, setRelationshipsVouched] = useState<string | null>(null);
  const eventProperties = { 'Database Type': databaseTypeName, 'Sync Target': sqlImportSyncTargetKind(syncTarget) };

  const trackRun = (isRelationship: boolean) =>
    void track('sqlImports', 'runQueryStarted', { ...eventProperties, 'Is Relationship Query': isRelationship });
  const trackFailure = (message: string) =>
    void track('sqlImports', 'runQueryFailed', { ...eventProperties, 'Error Message': message });

  const isObjectImport = draft.import_type === ImportType.object;
  const exampleRelationshipQuery = (fields: SqlImportRelationship) =>
    defaultSqlImportQuery({ databaseType: type, idFieldName: '', isObjectImport, relationship: fields });

  /**
   * The relationship the step shows. Until the draft has stored one, this is only an example: it
   * reaches the draft when the customer engages with it — edits a field, previews it, or overrides
   * a timeout to move on — and never merely because the step was opened. The backend keeps a
   * relationship once stored, so one written on the way past could not be taken back.
   */
  const stored = relationshipForStep(draft, objectTypes);
  const relationship = isEmptyString(stored.query) ? { ...stored, query: exampleRelationshipQuery(stored) } : stored;

  const previewBase = {
    databaseId: draft.database_id ?? '',
    importId: savedId ?? undefined,
    idFieldName: draft.id_field_name ?? '',
    location: draft.location,
    importType: draft.import_type,
    objectTypeId: draft.object_type_id,
  };
  const queryBody = buildPreviewBody({ ...previewBase, query: draft.query ?? '' });
  const relationshipsBody = buildPreviewBody({ ...previewBase, query: relationship.query, relationship });

  const queryRun = usePreviewRun({
    workspaceId,
    onOutcome: (successful, outcome) => {
      setQueryVouched(successful ? previewFingerprint(queryBody) : null);
      if (!successful) {
        trackFailure(outcomeMessage(outcome));
      }
    },
  });
  const relationshipsRun = usePreviewRun({
    workspaceId,
    onOutcome: (successful, outcome) => {
      setRelationshipsVouched(successful ? previewFingerprint(relationshipsBody) : null);
      if (!successful) {
        trackFailure(outcomeMessage(outcome));
      }
    },
  });

  /** The relationship the step shows becomes the draft's, so a save carries what was previewed. */
  const commitRelationship = () => setDraftFields({ relationship });

  /**
   * Change the relationship. While the query is still the example, it follows the new identifiers,
   * so the SQL aliases and the body agree; a query the customer edited is left alone.
   */
  const setRelationship = (changes: Partial<SqlImportRelationship>) => {
    const next = { ...relationship, ...changes };
    const query =
      changes.query == null && next.query === exampleRelationshipQuery(relationship)
        ? exampleRelationshipQuery(next)
        : next.query;
    relationshipsRun.invalidate();
    setDraftFields({ relationship: { ...next, query } });
  };

  /** A step is entered: a new import gets its example query, and Ember's "started" event fires. */
  const onEnterStep = (step: WizardStep) => {
    if (step === 'query') {
      if (savedId == null) {
        void track('sqlImports', 'writeQueryStarted', { 'Database Type': databaseTypeName });
      }
      if (isEmptyString(draft.query)) {
        setDraftFields({
          query: defaultSqlImportQuery({ databaseType: type, idFieldName: draft.id_field_name ?? '', isObjectImport }),
        });
      }
    }
    if (step === 'relationships') {
      if (savedId == null) {
        void track('sqlImports', 'writeRelationshipsQueryStarted', { 'Database Type': databaseTypeName });
      }
      // Settings may have moved the import's side (sync target, identifier) since the step stored
      // the relationship. Store the current sides so the saved import carries them too. A draft
      // with no relationship stored has nothing to resync, and gets nothing written here.
      const resynced = resyncedRelationship(draft, objectTypes, exampleRelationshipQuery);
      if (resynced != null) {
        setDraftFields({ relationship: resynced });
      }
    }
  };

  const hasSuccessfulQuery = queryVouched != null && queryVouched === previewFingerprint(queryBody);
  const hasSuccessfulRelationshipsQuery =
    relationshipsVouched != null && relationshipsVouched === previewFingerprint(relationshipsBody);

  return {
    hasSuccessfulQuery,
    hasSuccessfulRelationshipsQuery,
    queryRun,
    relationshipsRun,
    relationship,
    setRelationship,
    onEnterStep,
    /** Any edit to the query, or to a field the preview body reads, retires the run in flight. */
    setQuery: (query: string) => {
      queryRun.invalidate();
      setDraftFields({ query });
    },
    setLocation: (location: string) => {
      queryRun.invalidate();
      relationshipsRun.invalidate();
      setDraftFields({ location });
    },
    runQuery: () => {
      trackRun(false);
      void queryRun.run(queryBody);
    },
    runRelationshipsQuery: () => {
      trackRun(true);
      commitRelationship();
      void relationshipsRun.run(relationshipsBody);
    },
    /** The preview timed out; try again from a clean slate. */
    dismissTimeout: (isRelationship: boolean) => {
      void track('sqlImports', 'previewTimeoutDismiss', { 'Database Type': databaseTypeName });
      (isRelationship ? relationshipsRun : queryRun).clear();
    },
    /** The preview timed out; accept the query anyway and move on. */
    continueAfterTimeout: (isRelationship: boolean) => {
      void track('sqlImports', 'previewTimeoutContinue', { 'Database Type': databaseTypeName });
      if (isRelationship) {
        relationshipsRun.clear();
        setRelationshipsVouched(previewFingerprint(relationshipsBody));
        commitRelationship();
      } else {
        queryRun.clear();
        setQueryVouched(previewFingerprint(queryBody));
      }
      advance();
    },
    /**
     * Leave the relationships out of the import and move on. Nothing was written to the draft by
     * opening the step, so a draft that never stored a relationship simply keeps none. A draft that
     * did store one keeps it: the write body omits `relationship`, and the backend keeps a stored
     * relationship when the key is absent or null (`ui_api/sql_imports/create.go`, `toRelImport`),
     * as it did for Ember, whose skip set the model's relationship to null. Clearing a stored
     * relationship needs a backend change, tracked as FPS-3764.
     */
    skipRelationships: () => {
      void track('sqlImports', 'skipRelationshipsQuery', eventProperties);
      setDraftFields({ relationship: undefined });
      setRelationshipsVouched(null);
      advance();
    },
  };
}

export type QueryStepsState = ReturnType<typeof useQuerySteps>;

function outcomeMessage(
  outcome:
    | { status: 'result'; result: { error: string; validations: { errors: string[] } } }
    | { status: 'failed'; message: string },
): string {
  if (outcome.status === 'failed') {
    return outcome.message;
  }
  return isEmptyString(outcome.result.error) ? outcome.result.validations.errors.join(', ') : outcome.result.error;
}
